/**
 * Error handling utilities for the frontend.
 *
 * Provides consistent error handling patterns that:
 * - Log errors for debugging
 * - Return safe fallback values
 * - Optionally notify users
 */

// Simple console logger that can be swapped for a real logging service later
const logger = {
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    console.error(`[VizDay Error] ${message}`, { error, context });
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    console.warn(`[VizDay Warning] ${message}`, context);
  },
};

/**
 * Wraps a promise to catch errors and return a fallback value.
 * Unlike .catch(() => fallback), this logs the error for debugging.
 *
 * @param promise - The promise to wrap
 * @param fallback - Value to return on error
 * @param context - Context for error logging (e.g., API endpoint name)
 * @returns Promise that resolves to result or fallback
 *
 * @example
 * const data = await safeAsync(fetchUserData(), null, 'fetchUserData');
 */
export async function safeAsync<T>(
  promise: Promise<T>,
  fallback: T,
  context?: string
): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    logger.error(
      context ? `Error in ${context}` : 'Async operation failed',
      error,
      { context }
    );
    return fallback;
  }
}

/**
 * Wraps a promise to catch errors and return undefined.
 * Logs the error but doesn't throw.
 *
 * @param promise - The promise to wrap
 * @param context - Context for error logging
 * @returns Promise that resolves to result or undefined
 */
export async function safeAsyncOptional<T>(
  promise: Promise<T>,
  context?: string
): Promise<T | undefined> {
  try {
    return await promise;
  } catch (error) {
    logger.error(
      context ? `Error in ${context}` : 'Async operation failed',
      error,
      { context }
    );
    return undefined;
  }
}

/**
 * Creates a safe fetcher function for use with Promise.all.
 * Returns a function that wraps the API call with error handling.
 *
 * @param fetcher - The API function to call
 * @param fallback - Value to return on error
 * @param context - Context for error logging
 * @returns Wrapped function that returns fallback on error
 *
 * @example
 * const [users, posts] = await Promise.all([
 *   safeFetch(getUsers, [], 'getUsers')(),
 *   safeFetch(getPosts, [], 'getPosts')(),
 * ]);
 */
export function safeFetch<T, Args extends unknown[]>(
  fetcher: (...args: Args) => Promise<T>,
  fallback: T,
  context?: string
): (...args: Args) => Promise<T> {
  return async (...args: Args) => {
    try {
      return await fetcher(...args);
    } catch (error) {
      logger.error(
        context ? `Error in ${context}` : 'Fetch operation failed',
        error,
        { context, args }
      );
      return fallback;
    }
  };
}

/**
 * Extracts a user-friendly error message from various error types.
 *
 * @param error - The error to extract message from
 * @param defaultMessage - Fallback message if none found
 * @returns User-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  defaultMessage = 'An unexpected error occurred'
): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null) {
    // Handle API error responses
    const apiError = error as Record<string, unknown>;
    if (typeof apiError.error === 'string') {
      return apiError.error;
    }
    if (typeof apiError.message === 'string') {
      return apiError.message;
    }
    if (typeof apiError.detail === 'string') {
      return apiError.detail;
    }
  }

  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
}

/**
 * Logs an error with context without throwing.
 * Use for errors that shouldn't stop execution.
 *
 * @param error - The error that occurred
 * @param context - What was being attempted
 */
export function logError(error: unknown, context: string): void {
  logger.error(context, error);
}

/**
 * Logs a warning with context.
 * Use for non-critical issues.
 *
 * @param message - Warning message
 * @param context - Additional context
 */
export function logWarning(message: string, context?: Record<string, unknown>): void {
  logger.warn(message, context);
}

const errorUtils = {
  safeAsync,
  safeAsyncOptional,
  safeFetch,
  getErrorMessage,
  logError,
  logWarning,
};

export default errorUtils;
